import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Search, ShoppingCart, User, X } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useCart } from '../contexts/CartContext';
import api from '../utils/api';
import DarkModeToggle from './DarkModeToggle';
import TwineCandlesLogo from './TwineCandlesLogo';
import PromotionalBanner from './PromotionalBanner';

interface SearchSuggestion {
  _id: string;
  name: string;
  image?: string;
}

const getSuggestionImageUrl = (imagePath?: string): string => {
  if (!imagePath || imagePath === 'undefined' || imagePath === 'null') {
    return 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIGZpbGw9IiNFNUU3RUIiLz48cGF0aCBkPSJNMTggMThINDJWNDJIMThWMThaIiBmaWxsPSIjQ0JEMUYxIi8+PC9zdmc+';
  }

  if (imagePath.startsWith('http') || imagePath.startsWith('data:')) {
    return imagePath;
  }

  const normalizedPath = imagePath.replace(/\\/g, '/');
  const filename = normalizedPath.split('/').pop();
  const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';
  return `${apiUrl}/uploads/products/${filename}`;
};

const Header: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const [searchSuggestions, setSearchSuggestions] = useState<SearchSuggestion[]>([]);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);

  const { user, logout, isAdmin } = useAuth();
  const { totalItems } = useCart();
  const navigate = useNavigate();

  const desktopSearchRef = useRef<HTMLDivElement>(null);
  const mobileSearchRef = useRef<HTMLDivElement>(null);
  const desktopUserMenuRef = useRef<HTMLDivElement>(null);
  const mobileUserMenuRef = useRef<HTMLDivElement>(null);

  const showSuggestions = isSearchFocused && searchQuery.trim().length > 0 && searchSuggestions.length > 0;

  const closeTransientUI = () => {
    setIsSearchOpen(false);
    setIsSearchFocused(false);
    setSearchSuggestions([]);
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (!searchQuery.trim()) {
      return;
    }

    navigate(`/?search=${encodeURIComponent(searchQuery.trim())}`);
    setSearchQuery('');
    closeTransientUI();
  };

  const handleSuggestionClick = (productId: string) => {
    navigate(`/product/${productId}`);
    setSearchQuery('');
    closeTransientUI();
  };

  const handleLogout = () => {
    logout();
    navigate('/');
    setIsUserMenuOpen(false);
  };

  useEffect(() => {
    const trimmed = searchQuery.trim();
    if (!trimmed) {
      setSearchSuggestions([]);
      return;
    }

    const timer = window.setTimeout(async () => {
      try {
        const response = await api.get('/api/products', {
          params: {
            search: trimmed,
            limit: 3
          }
        });

        const products = Array.isArray(response.data?.products) ? response.data.products : [];
        setSearchSuggestions(
          products.slice(0, 3).map((product: any) => ({
            _id: product._id,
            name: product.name,
            image: Array.isArray(product.images) && product.images.length > 0 ? product.images[0] : ''
          }))
        );
      } catch (error) {
        console.error('Error fetching search suggestions:', error);
        setSearchSuggestions([]);
      }
    }, 220);

    return () => window.clearTimeout(timer);
  }, [searchQuery]);

  useEffect(() => {
    const handlePointerOutside = (event: MouseEvent | TouchEvent) => {
      const target = event.target as Node;

      const insideDesktopSearch = Boolean(desktopSearchRef.current?.contains(target));
      const insideMobileSearch = Boolean(mobileSearchRef.current?.contains(target));
      const insideDesktopUser = Boolean(desktopUserMenuRef.current?.contains(target));
      const insideMobileUser = Boolean(mobileUserMenuRef.current?.contains(target));

      if (!insideDesktopSearch && !insideMobileSearch) {
        setIsSearchOpen(false);
        setIsSearchFocused(false);
      }

      if (!insideDesktopUser && !insideMobileUser) {
        setIsUserMenuOpen(false);
      }
    };

    if (isSearchOpen || isSearchFocused || isUserMenuOpen) {
      document.addEventListener('mousedown', handlePointerOutside);
      document.addEventListener('touchstart', handlePointerOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handlePointerOutside);
      document.removeEventListener('touchstart', handlePointerOutside);
    };
  }, [isSearchOpen, isSearchFocused, isUserMenuOpen]);

  return (
    <header className="relative z-[100] shadow-lg border-b-2 border-secondary-200 dark:border-dark-border transition-all duration-300 bg-gradient-to-r from-slate-50 via-slate-100 to-slate-50 dark:bg-none dark:bg-dark-navbar">
      <div className="relative z-[100] dark:bg-dark-navbar dark:shadow-lg">
        <PromotionalBanner />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div dir="ltr" className="hidden md:grid items-center h-20 grid-cols-[1fr_minmax(20rem,34rem)_auto] gap-4 lg:gap-6">
            <div className="flex w-full items-center justify-between justify-self-start">
              <DarkModeToggle className="flex h-10 w-10 items-center justify-center shrink-0" iconClassName="h-6 w-6" />

              <Link
                to="/cart"
                className="relative flex h-10 w-10 shrink-0 items-center justify-center text-gray-700 dark:text-dark-text hover:text-accent-500 dark:hover:text-accent-300 transition-all duration-200 rounded-full hover:bg-secondary-50 dark:hover:bg-dark-hover group"
              >
                <ShoppingCart className="h-6 w-6 group-hover:scale-110 transition-transform duration-200" />
                {totalItems > 0 && (
                  <span className="absolute -top-1 -right-1 bg-gradient-to-r from-pink-500 to-pink-600 text-white text-xs rounded-full h-6 w-6 flex items-center justify-center font-bold shadow-lg animate-pulse">
                    {totalItems}
                  </span>
                )}
              </Link>

              <Link to="/?section=categories" className="inline-flex h-10 shrink-0 items-center text-gray-800 dark:text-dark-text hover:text-accent-500 dark:hover:text-accent-300 transition-all duration-200 px-3 rounded-lg hover:bg-secondary-50 dark:hover:bg-dark-hover font-medium">
                {'\u0639\u0631\u0636 \u0627\u0644\u0627\u0642\u0633\u0627\u0645'}
              </Link>

              <Link to="/" className="inline-flex h-10 shrink-0 items-center text-gray-800 dark:text-dark-text hover:text-accent-500 dark:hover:text-accent-300 transition-all duration-200 px-3 rounded-lg hover:bg-secondary-50 dark:hover:bg-dark-hover font-medium">
                {'\u0627\u0644\u0631\u0626\u064A\u0633\u064A\u0629'}
              </Link>

              {user ? (
                <div className="relative shrink-0" ref={desktopUserMenuRef}>
                  <button
                    onClick={() => setIsUserMenuOpen((prev) => !prev)}
                    className="flex items-center h-10 px-3 text-gray-700 dark:text-dark-text hover:text-primary-600 dark:hover:text-primary-400 transition-colors rounded-lg hover:bg-secondary-50 dark:hover:bg-dark-hover"
                  >
                    <User className="h-5 w-5" />
                    <span className="ml-2 text-sm">{user.name}</span>
                  </button>

                  {isUserMenuOpen && (
                    <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-dark-surface rounded-lg shadow-lg border border-gray-200 dark:border-dark-border z-[10000]">
                      {isAdmin && (
                        <>
                          <button type="button" className="block w-full text-right px-4 py-2 text-sm text-gray-700 dark:text-dark-text hover:bg-gray-100 dark:hover:bg-dark-hover" onClick={() => { setIsUserMenuOpen(false); navigate('/admin'); }}>
                            {'\u0644\u0648\u062D\u0629 \u0627\u0644\u062A\u062D\u0643\u0645'}
                          </button>
                          <button type="button" className="block w-full text-right px-4 py-2 text-sm text-gray-700 dark:text-dark-text hover:bg-gray-100 dark:hover:bg-dark-hover" onClick={() => { setIsUserMenuOpen(false); navigate('/admin/products'); }}>
                            {'\u0625\u062F\u0627\u0631\u0629 \u0627\u0644\u0645\u0646\u062A\u062C\u0627\u062A'}
                          </button>
                          <button type="button" className="block w-full text-right px-4 py-2 text-sm text-gray-700 dark:text-dark-text hover:bg-gray-100 dark:hover:bg-dark-hover" onClick={() => { setIsUserMenuOpen(false); navigate('/admin/orders'); }}>
                            {'\u0625\u062F\u0627\u0631\u0629 \u0627\u0644\u0637\u0644\u0628\u0627\u062A'}
                          </button>
                          <button type="button" className="block w-full text-right px-4 py-2 text-sm text-gray-700 dark:text-dark-text hover:bg-gray-100 dark:hover:bg-dark-hover" onClick={() => { setIsUserMenuOpen(false); navigate('/admin/analytics'); }}>
                            {'\u062A\u062D\u0644\u064A\u0644\u0627\u062A'}
                          </button>
                        </>
                      )}

                      <button
                        onClick={(e) => {
                          e.preventDefault();
                          handleLogout();
                        }}
                        className="block w-full text-right px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-b-lg"
                      >
                        {'\u062A\u0633\u062C\u064A\u0644 \u0627\u0644\u062E\u0631\u0648\u062C'}
                      </button>
                    </div>
                  )}
                </div>
              ) : null}
            </div>

            <div className="relative w-full" ref={desktopSearchRef}>
              <form onSubmit={handleSearch} className="relative">
                <div className="relative">
                  <Search className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-accent-500 dark:text-accent-300" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onFocus={() => setIsSearchFocused(true)}
                    placeholder={'\u0628\u062D\u062B'}
                    className="w-full pl-3 pr-9 py-2.5 border-2 border-secondary-200 dark:border-dark-border rounded-full focus:outline-none focus:ring-4 focus:ring-secondary-300 dark:focus:ring-primary-500 focus:border-secondary-400 dark:focus:border-primary-400 bg-white dark:bg-dark-surface text-gray-900 dark:text-dark-text placeholder-gray-500 dark:placeholder-dark-textSecondary transition-all duration-300 shadow-lg hover:shadow-xl"
                  />
                </div>
              </form>

              {showSuggestions && (
                <div className="absolute top-full mt-2 right-0 left-0 bg-white dark:bg-dark-surface border border-gray-200 dark:border-dark-border rounded-xl shadow-xl overflow-hidden z-[10001]">
                  {searchSuggestions.map((item) => (
                    <button
                      key={item._id}
                      type="button"
                      onClick={() => handleSuggestionClick(item._id)}
                      className="flex w-full items-center gap-3 text-right px-4 py-2.5 text-sm text-gray-800 dark:text-dark-text hover:bg-gray-100 dark:hover:bg-dark-hover transition-colors"
                    >
                      <img
                        src={getSuggestionImageUrl(item.image)}
                        alt={item.name}
                        className="h-9 w-9 rounded-md object-cover border border-gray-200 dark:border-dark-border shrink-0"
                        onError={(e) => {
                          e.currentTarget.src =
                            'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIGZpbGw9IiNFNUU3RUIiLz48cGF0aCBkPSJNMTggMThINDJWNDJIMThWMThaIiBmaWxsPSIjQ0JEMUYxIi8+PC9zdmc+';
                        }}
                      />
                      <span className="line-clamp-1">{item.name}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="flex items-center shrink-0 justify-self-end">
              <Link to="/" className="hover:scale-105 transition-transform duration-200">
                <TwineCandlesLogo size="navbar" />
              </Link>
            </div>
          </div>

          <div className="md:hidden flex items-center gap-2 h-16 px-3 border-b border-secondary-200 dark:border-dark-border bg-gradient-to-r from-secondary-50 via-secondary-100 to-secondary-50 dark:bg-none dark:from-dark-navbar dark:via-dark-navbar dark:to-dark-navbar">
            <div className="flex-shrink-0">
              <Link to="/">
                <TwineCandlesLogo size="sm" />
              </Link>
            </div>

            <div className="relative flex-1" ref={mobileSearchRef}>
              <form onSubmit={handleSearch} className="relative">
                <div className="relative">
                  <Search className="absolute right-2.5 top-1/2 transform -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onFocus={() => setIsSearchFocused(true)}
                    placeholder={'\u0628\u062D\u062B'}
                    className="w-full pl-3 pr-8 py-2 text-sm border border-gray-300 dark:border-dark-border rounded-full focus:outline-none focus:ring-2 focus:ring-secondary-300 dark:focus:ring-primary-500 focus:border-transparent bg-white dark:bg-dark-surface text-gray-900 dark:text-dark-text placeholder-gray-500 dark:placeholder-dark-textSecondary transition-colors duration-300 shadow"
                  />
                </div>
              </form>

              {showSuggestions && (
                <div className="absolute right-0 left-0 top-full mt-1 z-50 bg-white dark:bg-dark-surface border border-gray-200 dark:border-dark-border rounded-lg shadow-lg overflow-hidden">
                  {searchSuggestions.map((item) => (
                    <button
                      key={item._id}
                      type="button"
                      onClick={() => handleSuggestionClick(item._id)}
                      className="flex w-full items-center gap-2 text-right px-3 py-2 text-sm text-gray-800 dark:text-dark-text hover:bg-gray-100 dark:hover:bg-dark-hover transition-colors"
                    >
                      <img
                        src={getSuggestionImageUrl(item.image)}
                        alt={item.name}
                        className="h-8 w-8 rounded object-cover border border-gray-200 dark:border-dark-border shrink-0"
                        onError={(e) => {
                          e.currentTarget.src =
                            'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIGZpbGw9IiNFNUU3RUIiLz48cGF0aCBkPSJNMTggMThINDJWNDJIMThWMThaIiBmaWxsPSIjQ0JEMUYxIi8+PC9zdmc+';
                        }}
                      />
                      <span className="line-clamp-1">{item.name}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="flex items-center gap-1 shrink-0">
              <div className="order-3 flex items-center justify-center">
                <DarkModeToggle />
              </div>

              <Link
                to="/cart"
                className="relative order-2 p-1.5 text-gray-700 dark:text-dark-text hover:text-primary-600 dark:hover:text-primary-400 transition-colors flex items-center justify-center"
              >
                <ShoppingCart className="h-4 w-4" />
                {totalItems > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 bg-red-500 text-white text-xs rounded-full h-3.5 w-3.5 flex items-center justify-center text-[9px]">
                    {totalItems}
                  </span>
                )}
              </Link>

              {user && (
                <div className="relative order-4" ref={mobileUserMenuRef}>
                  <button
                    onClick={() => setIsUserMenuOpen((prev) => !prev)}
                    className="p-1.5 text-gray-700 dark:text-dark-text hover:text-primary-600 dark:hover:text-primary-400 transition-colors flex items-center justify-center"
                  >
                    <User className="h-4 w-4" />
                  </button>

                  {isUserMenuOpen && (
                    <div className="fixed inset-x-0 top-0 bg-white dark:bg-dark-surface shadow-lg z-50 md:hidden">
                      <div className="px-6 py-4 border-b border-gray-200 dark:border-dark-border">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-lg font-medium text-gray-900 dark:text-dark-text">{user.name}</p>
                            <p className="text-sm text-gray-500 dark:text-dark-textSecondary mt-1">{user.email}</p>
                          </div>
                          <button
                            onClick={() => setIsUserMenuOpen(false)}
                            className="p-2 text-gray-500 dark:text-dark-textSecondary hover:text-gray-700 dark:hover:text-dark-text"
                          >
                            <X className="h-6 w-6" />
                          </button>
                        </div>
                      </div>

                      <div className="px-6 py-4 space-y-2">
                        {isAdmin && (
                          <>
                            <button type="button" className="block w-full text-right px-4 py-2 text-sm text-gray-700 dark:text-dark-text hover:bg-gray-100 dark:hover:bg-dark-hover rounded-t-lg" onClick={() => { setIsUserMenuOpen(false); navigate('/admin'); }}>
                              {'\u0644\u0648\u062D\u0629 \u0627\u0644\u062A\u062D\u0643\u0645'}
                            </button>
                            <button type="button" className="block w-full text-right px-4 py-2 text-sm text-gray-700 dark:text-dark-text hover:bg-gray-100 dark:hover:bg-dark-hover" onClick={() => { setIsUserMenuOpen(false); navigate('/admin/products'); }}>
                              {'\u0625\u062F\u0627\u0631\u0629 \u0627\u0644\u0645\u0646\u062A\u062C\u0627\u062A'}
                            </button>
                            <button type="button" className="block w-full text-right px-4 py-2 text-sm text-gray-700 dark:text-dark-text hover:bg-gray-100 dark:hover:bg-dark-hover" onClick={() => { setIsUserMenuOpen(false); navigate('/admin/orders'); }}>
                              {'\u0625\u062F\u0627\u0631\u0629 \u0627\u0644\u0637\u0644\u0628\u0627\u062A'}
                            </button>
                            <button type="button" className="block w-full text-right px-4 py-2 text-sm text-gray-700 dark:text-dark-text hover:bg-gray-100 dark:hover:bg-dark-hover" onClick={() => { setIsUserMenuOpen(false); navigate('/admin/analytics'); }}>
                              {'\u062A\u062D\u0644\u064A\u0644\u0627\u062A'}
                            </button>
                          </>
                        )}

                        <button
                          onTouchEnd={(e) => {
                            e.preventDefault();
                            handleLogout();
                          }}
                          onClick={(e) => {
                            e.preventDefault();
                            handleLogout();
                          }}
                          className="block w-full text-right px-4 py-4 text-lg text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg touch-manipulation"
                        >
                          {'\u062A\u0633\u062C\u064A\u0644 \u0627\u0644\u062E\u0631\u0648\u062C'}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
